import { ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@restworld/utils-common';
import type { IconComponentType } from '../icon-button';

const badgeVariants = cva('inline-flex items-center py-1 px-2 rounded-lg text-[14px] h-[24px] font-medium w-fit', {
  variants: {
    variant: {
      primary: 'bg-white text-gray-600 font-normal border border-gray-400',
      secondary: 'bg-primary-50',
      tertiary: 'bg-primary-800 text-white rounded-full',
      error: 'bg-red-100 text-red-700',
      success: 'rounded bg-green-50 text-green-700',
      info: 'min-w-fit rounded bg-primary-50 text-primary-700',
      disabled: 'min-w-fit rounded bg-neutral-40 text-neutral-200',
      warning: 'min-w-fit rounded bg-orange-50 text-orange-700',
      danger: 'min-w-fit rounded bg-red-50 text-red-700',
      fresh: 'min-w-fit rounded bg-purple-500 text-neutral-10',
      neutral: 'min-w-fit rounded bg-neutral-30 text-neutral-700',
      restworld: 'min-w-fit rounded bg-purple-50 text-purple-700 w-fit',
    },
  },
  defaultVariants: {
    variant: 'primary',
  },
});

export interface IBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  children: ReactNode;
  variant?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'error'
    | 'success'
    | 'info'
    | 'disabled'
    | 'warning'
    | 'danger'
    | 'fresh'
    | 'neutral'
    | 'restworld';
  /** Optional leading icon rendered before the badge text. */
  icon?: IconComponentType;
}

export const Badge = ({ children, variant = 'primary', className, icon: Icon, ...rest }: IBadgeProps) => {
  return (
    <span className={cn(badgeVariants({ variant }), className)} {...rest}>
      {Icon ? <Icon className="mr-1 h-4 w-4" /> : null}
      {children}
    </span>
  );
};

export { badgeVariants };
export default Badge;
